import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  UserCheck, 
  UserX, 
  Mail, 
  Phone, 
  Calendar,
  Shield,
  Eye,
  Trash2,
  Download,
  UserPlus,
  Building,
  Settings
} from 'lucide-react';
import { api } from '../lib/api';
import OperatorUserTable from './OperatorUserTable';
import CreateOperatorUserModal from './CreateOperatorUserModal';
import EditOperatorUserModal from './EditOperatorUserModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: 'PASSENGER' | 'OPERATOR' | 'ADMIN' | 'MASTER_FIELD_OPERATOR' | 'OPERATOR_FIELD_OPERATOR';
  verified: boolean;
  createdAt: string;
  lastLogin?: string;
  bookingsCount?: number;
  fieldOperatorScopes?: Array<{
    operatorId: string;
    operator: {
      id: string;
      companyName: string;
    };
  }>;
}

interface OperatorUser {
  id: string;
  userId: string;
  operatorId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  verified: boolean;
  role: 'MANAGER' | 'DRIVER' | 'CONDUCTOR' | 'TICKETER' | 'MAINTENANCE';
  active: boolean;
  createdAt: string;
  operator?: {
    id: string;
    companyName: string;
    approved: boolean;
  };
}

interface Operator {
  id: string;
  companyName: string;
  approved: boolean;
}

interface UserRoleDetails {
  userId: string;
  email: string;
  primaryRole: User['role'];
  effectiveRoles: string[];
  operatorAssignment?: {
    id: string;
    operatorId: string;
    role: 'MANAGER' | 'DRIVER' | 'CONDUCTOR' | 'TICKETER' | 'MAINTENANCE';
    active: boolean;
  } | null;
  fieldOperatorScopes?: Array<{
    id: string;
    operatorId: string;
    operator?: {
      companyName: string;
    };
  }>;
}

const PLATFORM_ROLE_OPTIONS = [
  'ADMIN',
  'MASTER_FIELD_OPERATOR',
  'OPERATOR_FIELD_OPERATOR',
  'OPERATOR',
  'PASSENGER',
] as const;

const ADDITIONAL_ROLE_OPTIONS = [
  'OPERATOR',
  'OPERATOR_FIELD_OPERATOR',
] as const;

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const sanitizePhone = (phone: string) => phone.trim();

const validateEmailContent = (email: string): string | null => {
  const normalized = normalizeEmail(email);
  if (!normalized) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)) return 'Please enter a valid email address';
  if (normalized.includes('..')) return 'Email cannot contain consecutive dots';
  return null;
};

const validatePhoneContent = (phone: string): string | null => {
  const sanitized = sanitizePhone(phone);
  if (!sanitized) return 'Phone number is required';
  if (!/^[+\d\s()-]+$/.test(sanitized)) return 'Phone number contains invalid characters';
  if ((sanitized.match(/\+/g) || []).length > 1) return 'Phone number format is invalid';
  if (sanitized.includes(',') || sanitized.includes('/') || sanitized.includes(';')) return 'Please enter only one phone number';
  const digits = sanitized.replace(/\D/g, '');
  if (digits.length < 9 || digits.length > 15) return 'Phone number must have between 9 and 15 digits';
  return null;
};

const UserManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'operator-users'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [operatorUsers, setOperatorUsers] = useState<OperatorUser[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [operatorFilter, setOperatorFilter] = useState<string>('ALL');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateOperatorUserModal, setShowCreateOperatorUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreatePlatformUserModal, setShowCreatePlatformUserModal] = useState(false);
  const [createPlatformUserLoading, setCreatePlatformUserLoading] = useState(false);
  const [savingUserScope, setSavingUserScope] = useState(false);
  const [roleDetails, setRoleDetails] = useState<UserRoleDetails | null>(null);
  const [roleActionLoading, setRoleActionLoading] = useState(false);
  const [primaryRoleDraft, setPrimaryRoleDraft] = useState<User['role']>('PASSENGER');
  const [assignRoleForm, setAssignRoleForm] = useState({
    role: 'OPERATOR' as User['role'],
    operatorId: '',
    operatorRole: 'TICKETER' as 'MANAGER' | 'DRIVER' | 'CONDUCTOR' | 'TICKETER' | 'MAINTENANCE',
    operatorScopeIds: [] as string[],
  });

  const [createPlatformUserForm, setCreatePlatformUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'MASTER_FIELD_OPERATOR' as User['role'],
    verified: true,
    operatorScopeIds: [] as string[],
  });

  const [createOperatorUserForm, setCreateOperatorUserForm] = useState({
    operatorId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    role: 'TICKETER' as 'MANAGER' | 'DRIVER' | 'CONDUCTOR' | 'TICKETER' | 'MAINTENANCE'
  });
  
  const [editOperatorUserForm, setEditOperatorUserForm] = useState<{
    id: string;
    operatorId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: 'MANAGER' | 'DRIVER' | 'CONDUCTOR' | 'TICKETER' | 'MAINTENANCE';
    active: boolean;
  } | null>(null);
  
  const [showEditOperatorUserModal, setShowEditOperatorUserModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    console.log('UserManagement: Component mounted, fetching data...');
    fetchUsers();
    fetchOperatorUsers();
    fetchOperators();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('UserManagement: Starting fetchUsers...');
      
      // Check if token exists
      const token = localStorage.getItem('admin_token');
      console.log('UserManagement: Token exists:', !!token);
      
      if (!token) {
        console.log('UserManagement: No token found, redirecting to login');
        window.location.href = '/';
        return;
      }
      
      const response = await api.get('/users');
      console.log('UserManagement: API response received:', response);
      
      // Handle both direct array response and wrapped response
      const usersData = Array.isArray(response) ? response : (response.data || response);
      console.log('UserManagement: Processed users data:', {
        isArray: Array.isArray(usersData),
        length: Array.isArray(usersData) ? usersData.length : 'N/A',
        sample: Array.isArray(usersData) && usersData[0] ? {
          id: usersData[0].id?.substring(0, 8),
          name: `${usersData[0].firstName} ${usersData[0].lastName}`,
          email: usersData[0].email,
          role: usersData[0].role
        } : 'No sample available'
      });
      
      // Ensure we always set an array, even if the response is unexpected
      if (Array.isArray(usersData)) {
        setUsers(usersData);
      } else {
        console.error('UserManagement: Response is not an array, setting empty array');
        setUsers([]);
      }
      console.log('UserManagement: Users state updated successfully');
    } catch (error: any) {
      console.error('UserManagement: Error fetching users:', {
        message: error.message,
        status: error.status,
        response: error.response,
        stack: error.stack
      });
      
      // Always ensure users is an array even on error
      setUsers([]);
      
      // More detailed error handling
      if (error.status === 403) {
        alert('Access denied. Admin privileges required.');
      } else if (error.status === 401) {
        alert('Authentication failed. Please log in again.');
        // Clear invalid token and reload page
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.reload();
      } else {
        const errorMessage = error.message || error.toString() || 'Unknown error';
        alert(`Failed to load users: ${errorMessage}. Check console for details.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOperatorUsers = async () => {
    try {
      const response = await api.get('/admin/operator-users/all');
      const operatorUsersData = Array.isArray(response) ? response : (response.users || response.data || []);
      setOperatorUsers(operatorUsersData);
    } catch (error: any) {
      console.error('Error fetching operator users:', error);
      setOperatorUsers([]);
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await api.get('/operators');
      const operatorsData = Array.isArray(response)
        ? response
        : (response.operators || response.data?.operators || response.data || []);
      setOperators(operatorsData);
    } catch (error: any) {
      console.error('Error fetching operators:', error);
      setOperators([]);
    }
  };

  const handleCreateOperatorUser = async () => {
    const emailError = validateEmailContent(createOperatorUserForm.email);
    if (emailError) {
      alert(emailError);
      return;
    }

    const phoneError = validatePhoneContent(createOperatorUserForm.phone);
    if (phoneError) {
      alert(phoneError);
      return;
    }

    const payload = {
      ...createOperatorUserForm,
      email: normalizeEmail(createOperatorUserForm.email),
      phone: sanitizePhone(createOperatorUserForm.phone),
    };

    try {
      setCreateLoading(true);
      await api.post('/admin/operator-users/create', payload);
      
      // Reset form
      setCreateOperatorUserForm({
        operatorId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        role: 'TICKETER'
      });
      
      setShowCreateOperatorUserModal(false);
      await fetchOperatorUsers();
      alert('Operator user created successfully');
    } catch (error: any) {
      console.error('Error creating operator user:', error);
      alert(error.message || 'Failed to create operator user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreatePlatformUser = async () => {
    const emailError = validateEmailContent(createPlatformUserForm.email);
    if (emailError) {
      alert(emailError);
      return;
    }

    const phoneError = validatePhoneContent(createPlatformUserForm.phone);
    if (phoneError) {
      alert(phoneError);
      return;
    }

    const payload = {
      ...createPlatformUserForm,
      email: normalizeEmail(createPlatformUserForm.email),
      phone: sanitizePhone(createPlatformUserForm.phone),
    };

    try {
      setCreatePlatformUserLoading(true);
      await api.post('/users', payload);

      setCreatePlatformUserForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        role: 'MASTER_FIELD_OPERATOR',
        verified: true,
        operatorScopeIds: [],
      });

      setShowCreatePlatformUserModal(false);
      await fetchUsers();
      alert('Platform user created successfully');
    } catch (error: any) {
      console.error('Error creating platform user:', error);
      alert(error.message || 'Failed to create platform user');
    } finally {
      setCreatePlatformUserLoading(false);
    }
  };

  const handleSaveFieldOperatorScopes = async () => {
    if (!selectedUser || selectedUser.role !== 'OPERATOR_FIELD_OPERATOR') return;

    try {
      setSavingUserScope(true);
      const operatorScopeIds = (selectedUser.fieldOperatorScopes || []).map(scope => scope.operatorId);
      const response = await api.put(`/users/${selectedUser.id}/field-operator-scopes`, { operatorScopeIds });
      const scopes = Array.isArray(response?.scopes) ? response.scopes : [];

      setSelectedUser({
        ...selectedUser,
        fieldOperatorScopes: scopes.map((scope: any) => ({
          operatorId: scope.operatorId,
          operator: scope.operator,
        })),
      });

      await fetchUsers();
      alert('Field operator scope updated successfully');
    } catch (error: any) {
      console.error('Error saving field operator scope:', error);
      alert(error.message || 'Failed to update field operator scope');
    } finally {
      setSavingUserScope(false);
    }
  };

  const fetchUserRoleDetails = async (userId: string) => {
    try {
      const response = await api.get(`/users/${userId}/roles`);
      const details = (response?.data || response) as UserRoleDetails;
      setRoleDetails(details);
      setPrimaryRoleDraft(details.primaryRole);

      setSelectedUser(prev => {
        if (!prev) return prev;

        return {
          ...prev,
          role: details.primaryRole,
          fieldOperatorScopes: (details.fieldOperatorScopes || []).map(scope => ({
            operatorId: scope.operatorId,
            operator: {
              id: scope.operatorId,
              companyName: scope.operator?.companyName || 'Unknown Operator',
            },
          })),
        };
      });
    } catch (error: any) {
      console.error('Error fetching user role details:', error);
      setRoleDetails(null);
      alert(error.message || 'Failed to load user role details');
    }
  };

  const openUserDetailsModal = async (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
    setRoleDetails(null);
    setPrimaryRoleDraft(user.role);
    setAssignRoleForm({
      role: 'OPERATOR',
      operatorId: '',
      operatorRole: 'TICKETER',
      operatorScopeIds: [],
    });
    await fetchUserRoleDetails(user.id);
  };

  const handleAssignRole = async () => {
    if (!selectedUser) return;

    if (assignRoleForm.role === 'OPERATOR' && !assignRoleForm.operatorId) {
      alert('Please select an operator for OPERATOR role assignment');
      return;
    }

    if (assignRoleForm.role === 'OPERATOR_FIELD_OPERATOR' && assignRoleForm.operatorScopeIds.length === 0) {
      alert('Please select at least one operator scope for OPERATOR FIELD OPERATOR');
      return;
    }

    try {
      setRoleActionLoading(true);
      await api.post(`/users/${selectedUser.id}/roles/assign`, {
        role: assignRoleForm.role,
        operatorId: assignRoleForm.operatorId || undefined,
        operatorRole: assignRoleForm.operatorRole,
        operatorScopeIds: assignRoleForm.operatorScopeIds,
      });

      await fetchUsers();
      await fetchUserRoleDetails(selectedUser.id);
      alert('Role assigned successfully');
    } catch (error: any) {
      console.error('Error assigning role:', error);
      alert(error.message || 'Failed to assign role');
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleRemoveRole = async (role: User['role']) => {
    if (!selectedUser) return;

    if (!window.confirm(`Remove ${formatRoleLabel(role)} role from this user?`)) {
      return;
    }

    try {
      setRoleActionLoading(true);
      await api.post(`/users/${selectedUser.id}/roles/remove`, { role });
      await fetchUsers();
      await fetchUserRoleDetails(selectedUser.id);
      alert('Role removed successfully');
    } catch (error: any) {
      console.error('Error removing role:', error);
      alert(error.message || 'Failed to remove role');
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleSwitchPrimaryRole = async () => {
    if (!selectedUser) return;

    try {
      setRoleActionLoading(true);
      await api.put(`/users/${selectedUser.id}/roles/primary`, { role: primaryRoleDraft });
      await fetchUsers();
      await fetchUserRoleDetails(selectedUser.id);
      alert('Primary role updated successfully');
    } catch (error: any) {
      console.error('Error switching primary role:', error);
      alert(error.message || 'Failed to switch primary role');
    } finally {
      setRoleActionLoading(false);
    }
  };

  const handleEditOperatorUser = async () => {
    if (!editOperatorUserForm) return;

    const emailError = validateEmailContent(editOperatorUserForm.email);
    if (emailError) {
      alert(emailError);
      return;
    }

    const phoneError = validatePhoneContent(editOperatorUserForm.phone);
    if (phoneError) {
      alert(phoneError);
      return;
    }
    
    try {
      setEditLoading(true);
      const { id, ...updateData } = editOperatorUserForm;
      const payload = {
        ...updateData,
        email: normalizeEmail(updateData.email),
        phone: sanitizePhone(updateData.phone),
      };
      await api.put(`/admin/operator-users/${id}`, payload);
      
      setShowEditOperatorUserModal(false);
      setEditOperatorUserForm(null);
      await fetchOperatorUsers();
      alert('Operator user updated successfully');
    } catch (error: any) {
      console.error('Error updating operator user:', error);
      alert(error.message || 'Failed to update operator user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleOperatorUser = async (userId: string, currentStatus: boolean) => {
    try {
      const operatorUser = operatorUsers.find(u => u.id === userId);
      if (!operatorUser) return;

      await api.put(`/admin/operator-users/${userId}`, {
        ...operatorUser,
        active: !currentStatus
      });
      
      await fetchOperatorUsers();
      alert(`Operator user ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      console.error('Error toggling operator user:', error);
      alert(error.message || 'Failed to update operator user status');
    }
  };

  const handleDeleteOperatorUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this operator user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/admin/operator-users/${userId}`);
      await fetchOperatorUsers();
      alert('Operator user deleted successfully');
    } catch (error: any) {
      console.error('Error deleting operator user:', error);
      alert(error.message || 'Failed to delete operator user');
    }
  };

  const handleEditOperatorUserClick = (operatorUser: OperatorUser) => {
    setEditOperatorUserForm({
      id: operatorUser.id,
      operatorId: operatorUser.operatorId,
      firstName: operatorUser.firstName,
      lastName: operatorUser.lastName,
      email: operatorUser.email,
      phone: operatorUser.phone,
      role: operatorUser.role,
      active: operatorUser.active
    });
    setShowEditOperatorUserModal(true);
  };

  const handleUserAction = async (userId: string, action: 'verify' | 'unverify' | 'delete') => {
    try {
      if (action === 'delete') {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        
        await api.delete(`/users/${userId}`);
        setUsers(users.filter(u => u.id !== userId));
        alert('User deleted successfully');
      } else {
        const verified = action === 'verify';
        const user = users.find(u => u.id === userId);
        if (!user) return;

        await api.put(`/users/${userId}`, {
          ...user,
          verified
        });

        setUsers(users.map(u => 
          u.id === userId ? { ...u, verified } : u
        ));
        alert(`User ${verified ? 'verified' : 'unverified'} successfully`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user. Please try again.');
    }
  };

  const handleBulkAction = async (action: 'verify' | 'unverify' | 'delete') => {
    if (selectedUsers.length === 0) return;
    
    if (action === 'delete') {
      if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) return;
    }

    for (const userId of selectedUsers) {
      await handleUserAction(userId, action);
    }
    
    setSelectedUsers([]);
  };



  const exportUsers = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Role', 'Verified', 'Bookings', 'Created At'],
      ...filteredUsers.map(user => [
        `${user.firstName} ${user.lastName}`,
        user.email,
        user.phone,
        user.role,
        user.verified ? 'Yes' : 'No',
        user.bookingsCount?.toString() || '0',
        new Date(user.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transconnect-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Ensure arrays are always arrays before filtering
  const safeUsers = Array.isArray(users) ? users : [];
  const safeOperatorUsers = Array.isArray(operatorUsers) ? operatorUsers : [];
  
  const filteredUsers = safeUsers.filter(user => {
    const matchesSearch = 
      (user.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone || '').includes(searchTerm);
    
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = 
      statusFilter === 'ALL' || 
      (statusFilter === 'VERIFIED' && user.verified) ||
      (statusFilter === 'UNVERIFIED' && !user.verified);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const filteredOperatorUsers = safeOperatorUsers.filter(user => {
    const matchesSearch = 
      (user.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.phone || '').includes(searchTerm);
    
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = 
      statusFilter === 'ALL' || 
      (statusFilter === 'ACTIVE' && user.active) ||
      (statusFilter === 'INACTIVE' && !user.active);
    
    const matchesOperator = 
      operatorFilter === 'ALL' || 
      user.operatorId === operatorFilter ||
      (user.operator && user.operator.id === operatorFilter);

    return matchesSearch && matchesRole && matchesStatus && matchesOperator;
  });

  const userStats = {
    total: safeUsers.length,
    admins: safeUsers.filter(u => u.role === 'ADMIN').length,
    masterFieldOperators: safeUsers.filter(u => u.role === 'MASTER_FIELD_OPERATOR').length,
    operatorFieldOperators: safeUsers.filter(u => u.role === 'OPERATOR_FIELD_OPERATOR').length,
    operators: safeUsers.filter(u => u.role === 'OPERATOR').length,
    passengers: safeUsers.filter(u => u.role === 'PASSENGER').length,
    verified: safeUsers.filter(u => u.verified).length,
    unverified: safeUsers.filter(u => !u.verified).length
  };

  const operatorUserStats = {
    total: safeOperatorUsers.length,
    managers: safeOperatorUsers.filter(u => u.role === 'MANAGER').length,
    drivers: safeOperatorUsers.filter(u => u.role === 'DRIVER').length,
    conductors: safeOperatorUsers.filter(u => u.role === 'CONDUCTOR').length,
    ticketers: safeOperatorUsers.filter(u => u.role === 'TICKETER').length,
    maintenance: safeOperatorUsers.filter(u => u.role === 'MAINTENANCE').length,
    active: safeOperatorUsers.filter(u => u.active).length,
    inactive: safeOperatorUsers.filter(u => !u.active).length
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-100 text-red-800';
      case 'MASTER_FIELD_OPERATOR': return 'bg-purple-100 text-purple-800';
      case 'OPERATOR_FIELD_OPERATOR': return 'bg-indigo-100 text-indigo-800';
      case 'OPERATOR': return 'bg-blue-100 text-blue-800';
      case 'PASSENGER': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRoleLabel = (role: string) => role.replace(/_/g, ' ');



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  // Add error boundary for when users array is not properly set
  if (!Array.isArray(users)) {
    console.error('UserManagement: users is not an array:', users);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <Users className="h-12 w-12 mx-auto mb-2" />
            <h3 className="text-lg font-medium">Error Loading Users</h3>
            <p className="text-sm text-gray-600 mt-2">
              There was a problem loading the user data. Please refresh the page.
            </p>
          </div>
          <button
            onClick={fetchUsers}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage platform users and operator staff</p>
        </div>
        <div className="flex items-center space-x-3">
          {activeTab === 'users' && (
            <button
              onClick={() => setShowCreatePlatformUserModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <UserPlus className="h-4 w-4" />
              <span>Add Platform User</span>
            </button>
          )}
          {activeTab === 'operator-users' && (
            <button
              onClick={() => setShowCreateOperatorUserModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <UserPlus className="h-4 w-4" />
              <span>Add Operator User</span>
            </button>
          )}
          <button
            onClick={exportUsers}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('users');
              setSearchTerm('');
              setRoleFilter('ALL');
              setStatusFilter('ALL');
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Platform Users</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {userStats.total}
              </span>
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab('operator-users');
              setSearchTerm('');
              setRoleFilter('ALL');
              setStatusFilter('ALL');
              setOperatorFilter('ALL');
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'operator-users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4" />
              <span>Operator Staff</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {operatorUserStats.total}
              </span>
            </div>
          </button>
        </nav>
      </div>

      {/* Stats Cards */}
      {activeTab === 'users' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Total Users</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Admins</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.admins}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Master Field Ops</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.masterFieldOperators}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-indigo-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Operator Field Ops</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.operatorFieldOperators}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Operators</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.operators}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Passengers</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.passengers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Verified</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.verified}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <UserX className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Unverified</p>
                <p className="text-xl font-semibold text-gray-900">{userStats.unverified}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Total Staff</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Settings className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Managers</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.managers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Drivers</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.drivers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Conductors</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.conductors}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Ticketers</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.ticketers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <UserCheck className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Active</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.active}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <UserX className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Inactive</p>
                <p className="text-xl font-semibold text-gray-900">{operatorUserStats.inactive}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder={activeTab === 'users' ? "Search users by name, email, or phone..." : "Search operator staff by name, email, or phone..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            {activeTab === 'users' ? (
              <>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Roles</option>
                  <option value="ADMIN">Admin</option>
                  <option value="MASTER_FIELD_OPERATOR">Master Field Operator</option>
                  <option value="OPERATOR_FIELD_OPERATOR">Operator Field Operator</option>
                  <option value="OPERATOR">Operator</option>
                  <option value="PASSENGER">Passenger</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Status</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="UNVERIFIED">Unverified</option>
                </select>
              </>
            ) : (
              <>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Roles</option>
                  <option value="MANAGER">Manager</option>
                  <option value="DRIVER">Driver</option>
                  <option value="CONDUCTOR">Conductor</option>
                  <option value="TICKETER">Ticketer</option>
                  <option value="MAINTENANCE">Maintenance</option>
                </select>

                <select
                  value={operatorFilter}
                  onChange={(e) => setOperatorFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Operators</option>
                  {operators.map(operator => (
                    <option key={operator.id} value={operator.id}>
                      {operator.companyName}
                    </option>
                  ))}
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </>
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="mt-4 flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-sm text-blue-700">
              {selectedUsers.length} users selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkAction('verify')}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                Verify
              </button>
              <button
                onClick={() => handleBulkAction('unverify')}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
              >
                Unverify
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tables */}
      {activeTab === 'users' ? (
        /* Users Table */
        <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(filteredUsers.map(u => u.id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role & Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {(user.firstName || '?')[0]}{(user.lastName || '?')[0]}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {user.id.slice(0, 8)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="flex items-center text-gray-900">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {user.email}
                      </div>
                      <div className="flex items-center text-gray-600 mt-1">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {user.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {formatRoleLabel(user.role)}
                      </span>
                      <div>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.verified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        Joined {formatDate(user.createdAt)}
                      </div>
                      {user.lastLogin && (
                        <div className="mt-1">
                          Last seen {getTimeAgo(user.lastLogin)}
                        </div>
                      )}
                      {user.role === 'PASSENGER' && (
                        <div className="mt-1">
                          {user.bookingsCount} bookings
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openUserDetailsModal(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleUserAction(user.id, user.verified ? 'unverify' : 'verify')}
                        className={`p-2 rounded-md transition-colors ${
                          user.verified 
                            ? 'text-yellow-600 hover:bg-yellow-50' 
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={user.verified ? 'Unverify user' : 'Verify user'}
                      >
                        {user.verified ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleUserAction(user.id, 'delete')}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">
              {searchTerm || roleFilter !== 'ALL' || statusFilter !== 'ALL' 
                ? 'Try adjusting your search filters' 
                : 'No users have registered yet'
              }
            </p>
          </div>
        )}
      </div>
      ) : (
        /* Operator Users Table */
        <OperatorUserTable
          operatorUsers={filteredOperatorUsers}
          operators={operators}
          onEdit={handleEditOperatorUserClick}
          onToggle={handleToggleOperatorUser}
          onDelete={handleDeleteOperatorUser}
        />
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-xl font-medium text-blue-600">
                    {(selectedUser.firstName || '?')[0]}{(selectedUser.lastName || '?')[0]}
                  </span>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h4>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(selectedUser.role)}`}>
                    {formatRoleLabel(selectedUser.role)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-sm text-gray-900">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="text-sm text-gray-900">{selectedUser.phone}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    selectedUser.verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedUser.verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Member Since</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                </div>
                {selectedUser.lastLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Login</label>
                    <p className="text-sm text-gray-900">{getTimeAgo(selectedUser.lastLogin)}</p>
                  </div>
                )}
                {selectedUser.role === 'PASSENGER' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Total Bookings</label>
                    <p className="text-sm text-gray-900">{selectedUser.bookingsCount || 0}</p>
                  </div>
                )}
              </div>

              <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Primary Role</label>
                    <p className="text-xs text-gray-500">Controls default authorization context for the account.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={primaryRoleDraft}
                      onChange={(e) => setPrimaryRoleDraft(e.target.value as User['role'])}
                      className="px-3 py-2 border rounded-md text-sm"
                      disabled={roleActionLoading || !roleDetails}
                    >
                      {PLATFORM_ROLE_OPTIONS.map(role => (
                        <option key={role} value={role}>{formatRoleLabel(role)}</option>
                      ))}
                    </select>
                    <button
                      onClick={handleSwitchPrimaryRole}
                      disabled={roleActionLoading || !roleDetails}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Set Primary
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Effective Roles</label>
                  <div className="flex flex-wrap gap-2">
                    {(roleDetails?.effectiveRoles || [selectedUser.role]).map(role => (
                      <div key={role} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-white border text-xs">
                        <span className={`inline-flex px-2 py-0.5 rounded-full font-semibold ${getRoleBadgeColor(role)}`}>
                          {formatRoleLabel(role)}
                        </span>
                        {role !== 'PASSENGER' && (
                          <button
                            onClick={() => handleRemoveRole(role as User['role'])}
                            className="text-red-600 hover:text-red-700"
                            disabled={roleActionLoading}
                            title={`Remove ${formatRoleLabel(role)}`}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 border-t pt-3">
                  <label className="block text-sm font-medium text-gray-700">Assign Additional Role</label>
                  <p className="text-xs text-gray-500">Additional roles are additive only. To change ADMIN, MASTER FIELD OPERATOR, or PASSENGER use Primary Role above.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select
                      value={assignRoleForm.role}
                      onChange={(e) => setAssignRoleForm({
                        role: e.target.value as User['role'],
                        operatorId: '',
                        operatorRole: 'TICKETER',
                        operatorScopeIds: [],
                      })}
                      className="px-3 py-2 border rounded-md text-sm"
                      disabled={roleActionLoading}
                    >
                      {ADDITIONAL_ROLE_OPTIONS.map(role => (
                        <option key={role} value={role}>{formatRoleLabel(role)}</option>
                      ))}
                    </select>

                    {assignRoleForm.role === 'OPERATOR' && (
                      <select
                        value={assignRoleForm.operatorRole}
                        onChange={(e) => setAssignRoleForm({
                          ...assignRoleForm,
                          operatorRole: e.target.value as 'MANAGER' | 'DRIVER' | 'CONDUCTOR' | 'TICKETER' | 'MAINTENANCE',
                        })}
                        className="px-3 py-2 border rounded-md text-sm"
                        disabled={roleActionLoading}
                      >
                        <option value="MANAGER">Manager</option>
                        <option value="DRIVER">Driver</option>
                        <option value="CONDUCTOR">Conductor</option>
                        <option value="TICKETER">Ticketer</option>
                        <option value="MAINTENANCE">Maintenance</option>
                      </select>
                    )}
                  </div>

                  {assignRoleForm.role === 'OPERATOR' && (
                    <select
                      value={assignRoleForm.operatorId}
                      onChange={(e) => setAssignRoleForm({ ...assignRoleForm, operatorId: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md text-sm"
                      disabled={roleActionLoading}
                    >
                      <option value="">Select operator</option>
                      {operators.map(operator => (
                        <option key={operator.id} value={operator.id}>{operator.companyName}</option>
                      ))}
                    </select>
                  )}

                  {assignRoleForm.role === 'OPERATOR_FIELD_OPERATOR' && (
                    <div className="max-h-32 overflow-y-auto border rounded-md p-2 bg-white">
                      {operators.map(operator => {
                        const checked = assignRoleForm.operatorScopeIds.includes(operator.id);
                        return (
                          <label key={operator.id} className="flex items-center gap-2 py-1 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const operatorScopeIds = e.target.checked
                                  ? [...assignRoleForm.operatorScopeIds, operator.id]
                                  : assignRoleForm.operatorScopeIds.filter(id => id !== operator.id);
                                setAssignRoleForm({ ...assignRoleForm, operatorScopeIds });
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              disabled={roleActionLoading}
                            />
                            <span>{operator.companyName}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={handleAssignRole}
                      disabled={roleActionLoading}
                      className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {roleActionLoading ? 'Saving…' : 'Assign Role'}
                    </button>
                  </div>
                </div>
              </div>

              {selectedUser.role === 'OPERATOR_FIELD_OPERATOR' && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Operator Scope</label>
                      <p className="text-xs text-gray-500">Choose the operator(s) this field operator can manage.</p>
                    </div>
                    <button
                      onClick={handleSaveFieldOperatorScopes}
                      disabled={savingUserScope}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingUserScope ? 'Saving…' : 'Save Scope'}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {operators.map(operator => {
                      const checked = (selectedUser.fieldOperatorScopes || []).some(scope => scope.operatorId === operator.id);
                      return (
                        <label key={operator.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const currentScopes = selectedUser.fieldOperatorScopes || [];
                              const nextScopes = e.target.checked
                                ? [...currentScopes, { operatorId: operator.id, operator: { id: operator.id, companyName: operator.companyName } }]
                                : currentScopes.filter(scope => scope.operatorId !== operator.id);
                              setSelectedUser({ ...selectedUser, fieldOperatorScopes: nextScopes });
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>{operator.companyName}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleUserAction(selectedUser.id, selectedUser.verified ? 'unverify' : 'verify');
                    setShowUserModal(false);
                  }}
                  className={`px-4 py-2 text-white rounded-md transition-colors ${
                    selectedUser.verified 
                      ? 'bg-yellow-600 hover:bg-yellow-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {selectedUser.verified ? 'Unverify User' : 'Verify User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Operator User Modal */}
      <CreateOperatorUserModal
        show={showCreateOperatorUserModal}
        onClose={() => setShowCreateOperatorUserModal(false)}
        onSubmit={handleCreateOperatorUser}
        form={createOperatorUserForm}
        setForm={setCreateOperatorUserForm}
        operators={operators}
        loading={createLoading}
      />

      {showCreatePlatformUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create Platform User</h3>
              <button
                onClick={() => setShowCreatePlatformUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="px-3 py-2 border rounded-lg" placeholder="First name"
                value={createPlatformUserForm.firstName}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, firstName: e.target.value })} />
              <input className="px-3 py-2 border rounded-lg" placeholder="Last name"
                value={createPlatformUserForm.lastName}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, lastName: e.target.value })} />
              <input className="px-3 py-2 border rounded-lg" placeholder="Email"
                type="email"
                value={createPlatformUserForm.email}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, email: e.target.value })} />
              <input className="px-3 py-2 border rounded-lg" placeholder="Phone"
                type="tel"
                value={createPlatformUserForm.phone}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, phone: e.target.value })} />
              <input className="px-3 py-2 border rounded-lg" type="password" placeholder="Password"
                value={createPlatformUserForm.password}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, password: e.target.value })} />
              <select
                className="px-3 py-2 border rounded-lg"
                value={createPlatformUserForm.role}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, role: e.target.value as User['role'], operatorScopeIds: [] })}
              >
                {PLATFORM_ROLE_OPTIONS.map(role => (
                  <option key={role} value={role}>{formatRoleLabel(role)}</option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={createPlatformUserForm.verified}
                onChange={(e) => setCreatePlatformUserForm({ ...createPlatformUserForm, verified: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Verified user
            </label>

            {createPlatformUserForm.role === 'OPERATOR_FIELD_OPERATOR' && (
              <div className="mt-4 border rounded-lg p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">Operator Scope</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {operators.map(operator => {
                    const checked = createPlatformUserForm.operatorScopeIds.includes(operator.id);
                    return (
                      <label key={operator.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const operatorScopeIds = e.target.checked
                              ? [...createPlatformUserForm.operatorScopeIds, operator.id]
                              : createPlatformUserForm.operatorScopeIds.filter(id => id !== operator.id);
                            setCreatePlatformUserForm({ ...createPlatformUserForm, operatorScopeIds });
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{operator.companyName}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowCreatePlatformUserModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePlatformUser}
                disabled={createPlatformUserLoading}
                className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
              >
                {createPlatformUserLoading ? 'Creating…' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Operator User Modal */}
      <EditOperatorUserModal
        show={showEditOperatorUserModal}
        onClose={() => {
          setShowEditOperatorUserModal(false);
          setEditOperatorUserForm(null);
        }}
        onSubmit={handleEditOperatorUser}
        form={editOperatorUserForm}
        setForm={setEditOperatorUserForm}
        operators={operators}
        loading={editLoading}
      />
    </div>
  );
};

export default UserManagement;